import React, { useState } from 'react';
import { Send, CheckCircle, AlertCircle, HelpCircle } from 'lucide-react';
import PrivacyConsent from '../forms/PrivacyConsent';
import { facebookPixelEvents } from '../../utils/facebookPixel';

interface SignUpForDemoProps {
  onSuccess?: () => void;
}

const portalId = import.meta.env.VITE_HUBSPOT_PORTAL_ID;
const demoFormId = import.meta.env.VITE_HUBSPOT_DEMO_FORM_ID;

export default function SignUpForDemo({ onSuccess }: SignUpForDemoProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const formElement = e.target as HTMLFormElement;
    const formData = new FormData(formElement);
    const hutk = document.cookie.replace(/(?:(?:^|.*;\s*)hubspotutk\s*\=\s*([^;]*).*$)|^.*$/, "$1");

    try {
      // Track demo request
      facebookPixelEvents.lead({
        content_name: 'Demo Request',
        content_category: 'demo',
        content_type: 'form_submission'
      });

      const response = await fetch(`https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${demoFormId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fields: [
            {
              objectTypeId: "0-1",
              name: "firstname",
              value: formData.get('firstName')?.toString()
            },
            {
              objectTypeId: "0-1",
              name: "lastname",
              value: formData.get('lastName')?.toString()
            },
            {
              objectTypeId: "0-1",
              name: "email",
              value: formData.get('email')?.toString()
            },
            {
              objectTypeId: "0-1",
              name: "phone",
              value: formData.get('phone')?.toString() || ""
            },
            { 
              objectTypeId: "0-1",
              name: 'role',
              value: formData.get('role')?.toString()
            },
            { 
              objectTypeId: "0-1",
              name: 'type_of_homeschooling',
              value: formData.get('type_of_homeschooling')?.toString()
            },
            { 
              objectTypeId: "0-1",
              name: 'student_age',
              value: formData.get('student_age')?.toString()
            }
          ],
          context: {
            pageUri: window.location.href,
            pageName: document.title,
            ...(hutk && { hutk })
          },
          legalConsentOptions: {
            consent: {
              consentToProcess: true,
              text: "EmploSkills is committed to protecting and respecting your privacy, and we'll only use your personal information to administer your account and to provide the products and services you requested from us.",
              communications: [
                {
                  value: Boolean(formData.get('marketingConsent')),
                  subscriptionTypeId: 999,
                  text: "I agree to receive communications from EmploSkills"
                }
              ]
            }
          }
        })
      });

      if (response.ok) {
        facebookPixelEvents.completeRegistration({
          content_name: 'Demo Registration Complete',
          content_category: 'demo',
          content_type: 'registration_success'
        });
      } else {
        throw new Error('Submission failed');
      }
      
      setSubmitStatus('success');
      if (onSuccess) {
        setTimeout(onSuccess, 1500);
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {submitStatus === 'success' && (
        <div className="bg-green-50 p-4 rounded-md flex items-center text-green-700">
          <CheckCircle className="w-5 h-5 mr-2" />
          Successfully signed up for the demo!
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="bg-red-50 p-4 rounded-md flex items-center text-red-700">
          <AlertCircle className="w-5 h-5 mr-2" />
          Something went wrong. Please try again.
        </div>
      )}

      <div>
        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
          First Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="firstName"
          name="firstName"
          required
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          disabled={isSubmitting}
          placeholder="John"
        />
      </div>

      <div>
        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
          Last Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="lastName"
          name="lastName"
          required
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          disabled={isSubmitting}
          placeholder="Doe"
        />
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
          Email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          required
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          disabled={isSubmitting}
          placeholder="john@example.com"
        />
      </div>

      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
          Phone Number <span className="text-gray-400 text-xs">(optional)</span>
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          disabled={isSubmitting}
          placeholder="+1 (555) 555-5555"
        />
      </div>
      <div>
        <div className="flex items-center gap-1">
          <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
            Role <span className="text-red-500">*</span>
          </label>
          <div className="relative group">
            <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
            <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-sm rounded-md shadow-lg z-50">
              <div className="relative">
                Please specify what type of HomeSchooling arrangement/type you have. This will help us share the relevant information with you
                <div className="absolute top-1/2 -translate-y-1/2 -left-1 w-2 h-2 bg-gray-900 rotate-45"></div>
              </div>
            </div>
          </div>
        </div>
        <select
          id="role"
          name="role"
          required
          className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm p-2"
        >
          <option value="">Select Type</option>
          <option value="Homeschool Parent">Homeschool Parent</option>
          <option value="Homeschool Teacher">Homeschool Teacher</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div>
        <div className="flex items-center gap-1">
          <label htmlFor="type_of_homeschooling" className="block text-sm font-medium text-gray-700 mb-1">
            Type of Homeschool <span className="text-red-500">*</span>
          </label>
          <div className="relative group">
            <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
            <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-sm rounded-md shadow-lg z-50">
              <div className="relative">
                Please specify what type of HomeSchooling arrangement/type you have. This will help us share the relevant information with you
                <div className="absolute top-1/2 -translate-y-1/2 -left-1 w-2 h-2 bg-gray-900 rotate-45"></div>
              </div>
            </div>
          </div>
        </div>
        <select
          id="type_of_homeschooling"
          name="type_of_homeschooling"
          required
          className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm p-2"
        >
          <option value="">Select Type</option>
          <option value="Individual household">Individual Homeschool</option>
          <option value="Homeschooling Cooperative">Homeschooling Cooperative</option>
          <option value="Micro School">Micro School</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div>
        <div className="flex items-center gap-1">
          <label htmlFor="student_age" className="block text-sm font-medium text-gray-700 mb-1">
            What age group are your Homeschoolers? <span className="text-red-500">*</span>
          </label>
          <div className="relative group">
            <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
            <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-sm rounded-md shadow-lg z-50">
              <div className="relative">
                To help us share the relevant information with you, please specify what age group your Homeschoolers are in.
                <div className="absolute top-1/2 -translate-y-1/2 -left-1 w-2 h-2 bg-gray-900 rotate-45"></div>
              </div>
            </div>
          </div>
        </div>
        <select
          id="student_age"
          name="student_age"
          required
          className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm p-2"
        >
          <option value="">Select Age Group</option>
          <option value="Middle School">Middle School</option>
          <option value="High School">High School</option>
          <option value="Middle and High School">Middle and High School</option>
          <option value="Other">Other</option>
        </select>
      </div>




      <div className="mt-6">
        <PrivacyConsent 
          onConsentChange={(field, value) => console.log(field, value)}
          required={true}
        />
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className={`w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white transition-all duration-200 ${
          isSubmitting 
            ? 'bg-blue-400 cursor-not-allowed' 
            : 'bg-primary hover:bg-blue-700'
        }`}
      >
        <Send className="w-5 h-5 mr-2" />
        {isSubmitting ? 'Signing Up...' : 'Sign Up for Demo'}
      </button>
    </form>
  );
} 